import {UID} from '../../shared/symbols';
import {Submission, SubmissionScanQuality} from '../../dashboard/symbols/submission.symbols';
import {DashboardChartDataUtilsComparisonResult} from './dashboard-chart-data-compare-percentage-utils.symbols';
import {unifiedSerializer} from '../../shared/unified-serializer';

/** Raw data with percentiles derivative. */
export interface DashboardChartDataByScan {
  /** Unique id related to the current model. */
  id: UID;

  /** Describes with derivative data. */
  data: {
    meta: {[brainPart: string]: DashboardChartRawDataByScan};
  };
}

/**
 * Describes calculated parameters for chart after extracting from the raw data.
 */
export interface DashboardBrainPartCalculatedData {
  /** Percentage of the whole brain that is occupied by the current brain part. */
  fraction: number;

  /** Volume of the brain part in cm3. */
  volume: number;

  /** Contains a percentage of the user's with a higher brain region size ratio around all the users. */
  percentOfUsersWithLowerRatio: number;
}

/** Contain relation between brain part name and its fraction data. */
export type DashboardBrainPartCalculatedDataRelation = {[brainPartName: string]: DashboardBrainPartCalculatedData};

/**
 * Describes detailed white matter data extracted by backend from the related scan type.
 */
export interface DashboardChartWhiteMatterData {
  /** White matter subregion name. */
  region: string;

  /** Calculated relative score describing the brain part size. */
  score: number;

  /** Volume of the brain part in cm3. */
  volume: number;

  /** Percentage of the whole brain that is occupied by the current brain part. */
  percents: number;
}

/**
 * Describes relation between region name and its white matter data.
 */
export type DashboardChartWhiteMatterDataRelation = Map<string, DashboardChartWhiteMatterData>;

/**
 * Describes detailed PVS data extracted by backend from the related scan type.
 */
export interface DashboardChartPvsData {
  /** PVS subregion name. */
  name: string;

  /** Calculated relative score describing the brain part size. */
  score: number;

  /** Calculated PVS count. */
  count: number;

  /** Volume of the brain part in cm3. */
  volume: number;
}

/**
 * Describes relation between region name and its white matter data.
 */
export type DashboardChartPvsDataRelation = Map<string, DashboardChartPvsData>;

/**
 * Describes brain age data calculated by backend based on T1 scan.
 */
export interface DashboardChartBrainAgeData {
  /** User's real age. */
  age: number;

  /** Calculated user's brain age. */
  corrected_brain_age: number;

  /** Difference between the user's brain age and the user's real age. */
  age_diff: number;
}

/** Contain relation between submission ID and its brain age data. */
export type DashboardChartBrainAgeDataRelation = Record<number, DashboardChartBrainAgeData>;

/**
 * Describes calculated parameters for chart typical zones after extracting from the raw data.
 */
export interface DashboardBrainPartTypicalRange {
  /** Minimal presented age in the whole statistics range. */
  minimalPresentedAge: number;

  /** Maximal presented age in the whole statistics range. */
  maximalPresentedAge: number;

  /** Minimal fraction value presented in the whole statistics range. */
  minimalBottomLinePoint: number;

  /** Maximal fraction value presented in the whole statistics range. */
  maximalUpperLinePoint: number;

  /** Data points representing the bottom line of the typical range zone. */
  bottomLimitLinePoints: DashboardChartDataTypicalZonePoint[];

  /** Data points representing the upper line of the typical range zone. */
  upperLimitLinePoints: DashboardChartDataTypicalZonePoint[];
}

/**
 * Raw section data for chart.
 * Used for creating zones on the chart with typical ranges.
 * Provides data about points for two lines of the typical range zone: upper and bottom.
 * These points can be converted to lines using interpolation.
 * Two lines are used for creating a zone, where upper and bottom borders are defined by these lines,
 * and the left and right borders are defined by the age.
 */
export interface DashboardChartDataTypicalZone {
  /**
   * Brain part name.
   * It seems like it is defined manually on the backend, so it is safer to use tag as a unique identifier.
   */
  name: string;

  /**
   * Brain part tag.
   * Formed from brain part name, but it is always snake case.
   * For example, (name: 'Cerebellum' -> tag: 'cerebellum'), (name: 'White Matter' -> tag: 'white_matter').
   */
  tag: string;

  /**
   * Represents a chart's upper line of the typical range zone.
   * It contains a list of points that can be converted to a line using interpolation.
   */
  line_95: DashboardChartDataTypicalZonePoint[];

  /**
   * Represents a chart's bottom line of the typical range zone.
   * It contains a list of points that can be converted to a line using interpolation.
   */
  line_05: DashboardChartDataTypicalZonePoint[];

  /**
   * The maximal fraction value presented in the upper line.
   */
  line_95_max: number;

  /**
   * The minimal fraction value presented in the bottom line.
   */
  line_05_min: number;
}

/**
 * Represents a point that describes a chart line.
 * First value is an age, second value is a fraction.
 */
export type DashboardChartDataTypicalZonePoint = [number, number];

/**
 * Describe calculated statistic data for each region.
 * Backend provides this data from KeyLayer derivative.
 */
export interface DashboardChartRawDataByScan {
  /** Volume of the brain part in cm3. */
  'raw value': number;

  /** Percentage of the whole brain that is occupied by the current brain part. */
  'percent of whole brain': number;

  /**
   * Percentage of the user's brain region size ratio around all the users.
   * Where the percent describes the number of users with a lower brain region size ratio.
   * 0-4% considered as a 'Low' value.
   * 5-95% considered as a 'Typical' value.
   * 96-100% considered as a 'High' value.
   * This legend is inverted for Ventricle regions.
   */
  Centile: number;
}

/**
 * Describes the configuration of a horizontal line in a graph.
 * It is placed on the left of the chart and contains unified data about general brain part ranges.
 */
export interface DashboardChartDataHorizontalLineConfig {
  /** Added width for the bar on the chart. */
  width: number;

  /** Added height for the bar on the chart. */
  height: number;

  /** The color of the line. */
  color: string;

  /** The label of the line. */
  label: string;
}

/**
 * Describes all necessary fields for creating a chart.
 */
export interface BrainSectionChartConfig {
  /**
   * Information about the related submissions.
   * Used for displaying data points on the chart.
   */
  submissionChartRelatedData: DashboardChartDataSubmissionDetails[];

  /**
   * Min possible value for the chart.
   * Used for defining the chart's bottom limit.
   */
  bottomChartLimit: number;

  /**
   * Max possible value for the chart.
   * Used for defining the chart's upper limit.
   */
  upperChartLimit: number;

  /**
   * Min possible value for the age.
   * Used for the chart's left limit label.
   */
  leftAgeLimit: number;

  /**
   * Max possible value for the age.
   * Used for the chart's right limit label.
   */
  rightAgeLimit: number;

  /**
   * Defined left limit for the chart.
   */
  leftChartLimit: number;

  /**
   * Defined right limit for the chart.
   */
  rightChartLimit: number;

  /**
   * Data points for drawing a typical range zone.
   */
  typicalZonePoints: DashboardChartDataTypicalZonePoint[];

  /**
   * Data points for drawing a lower values range zone.
   */
  lowerZonePoints: DashboardChartDataTypicalZonePoint[];

  /**
   * Data for drawing horizontal bars with unified chart data.
   */
  horizontalBarsLine: DashboardChartDataHorizontalLineConfig[];

  /**
   * Contain additional information about the chart generation process.
   */
  chartMetadata: DashboardChartMetadata;
}

/**
 * Describes chart metadata.
 * It is not used for drawing the chart, but it is used for displaying additional information about the chart.
 */
export interface DashboardChartMetadata {
  /** The status of the chart generation process. */
  processStatus: DashboardChartMetadataStatus;

  /** Identifier of the error if the process status is an error. */
  errorIdentifier: DashboardChartMetadataErrorIdentifier;
}

/**
 * Describes possible statuses for the chart metadata.
 */
export enum DashboardChartMetadataStatus {
  /** The chart generation finished successfully. */
  Success = 'success',

  /** The chart generation is interrupted. */
  Error = 'error',

  /** The chart should be hidden without any message or error. */
  ShouldBeHidden = 'hidden',
}

/**
 * Describes possible error identifiers for the chart metadata.
 */
export enum DashboardChartMetadataErrorIdentifier {
  /** The scan quality is too low (>2). */
  LowQuality = 'low-quality',

  /** The scan is marked as high contrast. */
  HighContrast = 'high-contrast',

  /** The user doesn't have access to the chart. */
  OnlyForPhysicians = 'only-for-physicians',

  /** The user's age at scan is too low. */
  LowAge = 'low-age',

  /** The user's age at scan is too high. */
  HighAge = 'high-age',

  /** Some data needed for the chart is missing. */
  MissingData = 'missing-data',

  /** Something went wrong for an unknown reason. */
  Unknown = 'unknown',
}

/**
 * Contains details about submission needed for data point on the chart.
 * It displays as filled point if it is the currently selected submission.
 * Otherwise, it displays as an empty point.
 */
export interface DashboardChartDataSubmissionDetails {
  /** Unique submission id. */
  id: Submission['id'];

  /** Indicates if the submission is public. */
  is_public: Submission['is_public'];

  /** Indicates if the submission is shared. */
  is_shared: Submission['is_shared'];

  /** User's age at the moment of the scan. */
  ageAtScan: number;

  /** Date of the scan. */
  scanDate: Date;

  /** Volume of the brain part in cm3. */
  volume: number;

  /** Percentage of the whole brain that is occupied by the current brain part. */
  fraction: number;

  /** Indicates if the submission is selected (data point should be filled). */
  isSelected: boolean;

  /** Contains a comparison result between the user's data and typical range. */
  comparisonResult: DashboardChartDataUtilsComparisonResult;

  /** Describes the scan quality of the submission. */
  scanQuality: SubmissionScanQuality;

  /** Describes the region size in comparison with the other users in this age and sex group. */
  normativePercentile: number;
}

/**
 * The backend provides the wrong naming for some properties.
 * This function fixes the naming by adding the correct ones.
 */
export const typicalRangesFixWrongPartNaming = (typicalRanges: DashboardChartDataTypicalZone[]): DashboardChartDataTypicalZone[] => {
  return typicalRanges.map(typicalRange => {
    // Replace the wrong naming with the correct one.
    switch (typicalRange.name) {
      case 'Caudate':
        return {
          ...typicalRange,
          name: 'Caudate Nucleus',
        };
      case 'Left Caudate':
        return {
          ...typicalRange,
          name: 'Left Caudate Nucleus',
          tag: 'left_caudate_nucleus',
        };
      case 'Right Caudate':
        return {
          ...typicalRange,
          name: 'Right Caudate Nucleus',
          tag: 'right_caudate_nucleus',
        };
    }

    return typicalRange;
  });
}

/**
 * The backend provides the wrong letter casing for some properties.
 * Some properties are provided with the wrong naming (not unified with the rest of the properties).
 * This function fixes the casing by adding the correct ones.
 */
export const chartDataByScanFixWrongPartNaming = (chartDataByScan: DashboardChartDataByScan): DashboardChartDataByScan => {
  // Extract provided configs for the matched property.
  const brainPartsData = chartDataByScan?.data?.meta;

  // The backend provides the wrong letter casing for some properties.
  const fixedNames = unifiedSerializer<{[p: string]: DashboardChartRawDataByScan}, {[p: string]: DashboardChartRawDataByScan}>(
    brainPartsData,
    {
      Caudate: {
        propertyToWrite: 'Caudate Nucleus',
        serializeMethod: data => data,
        shouldBeWritten: data => !!data,
      },
      'Left Caudate': {
        propertyToWrite: 'Left Caudate Nucleus',
        serializeMethod: data => data,
        shouldBeWritten: data => !!data,
      },
      'Right Caudate': {
        propertyToWrite: 'Right Caudate Nucleus',
        serializeMethod: data => data,
        shouldBeWritten: data => !!data,
      },
      'Right Inferior lateral ventricle': {
        propertyToWrite: 'Right Inferior Lateral Ventricle',
        serializeMethod: data => data,
        shouldBeWritten: data => !!data,
      },
      'Left Inferior lateral ventricle': {
        propertyToWrite: 'Left Inferior Lateral Ventricle',
        serializeMethod: data => data,
        shouldBeWritten: data => !!data,
      },
      'Inferior lateral ventricle': {
        propertyToWrite: 'Inferior Lateral Ventricle',
        serializeMethod: data => data,
        shouldBeWritten: data => !!data,
      },
    }
  );

  // Return the fixed data.
  return {
    ...chartDataByScan,
    data: {
      ...chartDataByScan?.data,
      meta: {
        ...chartDataByScan?.data?.meta,
        ...fixedNames,
      },
    },
  };
};
